import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import { db } from '~/firebase.client'
import Lightbox from '~/components/Lightbox'

/** Force http:// URLs to https:// to prevent mixed-content warnings */
function https(url) { return url ? url.replace(/^http:\/\//i, 'https://') : url }

/** Turn URLs in plain text into clickable links */
function Linkify({ children }) {
  if (typeof children !== 'string') return children
  const urlRegex = /((?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be|instagram\.com|facebook\.com|fb\.com|whatsapp\.com|wa\.me|twitter\.com|x\.com|linkedin\.com|t\.me)[^\s]*|https?:\/\/[^\s]+)/gi
  const parts = children.split(urlRegex)
  return parts.map((part, i) => {
    if (!urlRegex.test(part)) return part
    const href = part.match(/^https?:\/\//) ? part : `https://${part}`
    return <a key={i} href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#1a56db', wordBreak: 'break-all' }}>{part}</a>
  })
}

function ProductCard({ product, onImageClick }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <article className="product-card">
      <div className="product-card-media" onClick={() => product.imageUrl && onImageClick()} style={{ cursor: product.imageUrl ? 'pointer' : 'default' }}>
        {product.imageUrl ? (
          <img
            src={https(product.imageUrl)}
            alt={product.name}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="product-card-placeholder" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.25 }}>
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            </svg>
          </div>
        )}
        {product.featured && (
          <span className="product-badge">Featured</span>
        )}
      </div>
      <div className="product-card-body">
        {product.categoryName && (
          <span className="product-category-tag">{product.categoryName}</span>
        )}
        <h3 className="product-card-title">{product.name}</h3>
        {product.modelNumber && (
          <p className="product-model">Model: {product.modelNumber}</p>
        )}
        {product.description && (
          <p className="product-card-desc"><Linkify>{product.description}</Linkify></p>
        )}
        {product.specs?.length > 0 && (
          <div className="product-specs-wrap">
            <button
              className="product-specs-toggle"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              {expanded ? '▲' : '▼'} Specifications
            </button>
            {expanded && (
              <table className="product-specs-table">
                <tbody>
                  {product.specs.map((s, i) => (
                    <tr key={i}>
                      <td>{s.key}</td>
                      <td><Linkify>{s.value}</Linkify></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </article>
  )
}

export default function ProductsPage() {
  const [categories, setCategories] = useState([])
  const [products, setProducts] = useState([])
  const [activeCategory, setActiveCategory] = useState('all')
  const [loading, setLoading] = useState(true)
  const [lightbox, setLightbox] = useState(null)
  const productsGridRef = useRef(null)

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'categories'), orderBy('name')),
      (snap) => setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    )
    return unsub
  }, [])

  useEffect(() => {
    setLoading(true)
    const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'))
    const unsub = onSnapshot(
      q,
      (snap) => {
        setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
        setLoading(false)
      },
      (err) => {
        console.warn('Products query error:', err.message)
        setProducts([])
        setLoading(false)
      },
    )
    return unsub
  }, [])

  const scrollToProducts = () => {
    requestAnimationFrame(() => {
      const el = productsGridRef.current
      if (!el) return
      // Use fixed offset to avoid forced reflow from offsetHeight/getComputedStyle
      const offset = 180
      const top = el.getBoundingClientRect().top + window.scrollY - offset
      window.scrollTo({ top, behavior: 'smooth' })
    })
  }

  const filteredProducts = activeCategory === 'all'
    ? products
    : products.filter((p) => p.categoryId === activeCategory)

  // Count products per category
  const catCounts = {}
  products.forEach((p) => {
    if (p.categoryId) {
      catCounts[p.categoryId] = (catCounts[p.categoryId] || 0) + 1
    }
  })

  return (
    <main id="products-main">
      {/* Page header */}
      <section className="products-hero section-pad muted">
        <div className="container">
          <span className="eyebrow">Our Product Range</span>
          <h1 className="page-hero-title">Machines &amp; Equipment</h1>
          <p className="page-hero-desc">
            Browse our complete range of VICTORY digital printing machines, cutting
            equipment, inks, and finishing solutions.
          </p>
        </div>
      </section>

      {/* Category filter — sticky on scroll */}
      {categories.length > 0 && (
        <div className="product-cat-filter-wrap">
          <div className="container">
            <div className="product-cat-filter-grid">
              <button
                className={`product-cat-filter-card ${activeCategory === 'all' ? 'product-cat-filter-card--active' : ''}`}
                onClick={() => { setActiveCategory('all'); scrollToProducts() }}
              >
                <span className="product-cat-filter-count">{products.length}</span>
                <span className="product-cat-filter-name">All Products</span>
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`product-cat-filter-card ${activeCategory === cat.id ? 'product-cat-filter-card--active' : ''}`}
                  onClick={() => { setActiveCategory(cat.id); scrollToProducts() }}
                >
                  <span className="product-cat-filter-count">{catCounts[cat.id] || 0}</span>
                  <span className="product-cat-filter-name">{cat.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Products grid */}
      <section className="section-pad" ref={productsGridRef}>
        <div className="container">
          {loading ? (
            <div className="products-loading">
              <div className="products-loading-spinner" aria-hidden="true" />
              <p>Loading products…</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <p style={{ fontSize: '1.1rem', color: 'var(--color-muted)' }}>
                No products found in this category.
              </p>
              <Link className="btn btn-ghost-light" to="/contact" style={{ marginTop: 16 }}>
                Enquire About a Machine
              </Link>
            </div>
          ) : (
            <div className="products-grid">
              {filteredProducts.map((prod) => (
                <ProductCard
                  key={prod.id}
                  product={prod}
                  onImageClick={() => setLightbox({ images: [{ url: https(prod.imageUrl) }], index: 0 })}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {lightbox && (
        <Lightbox
          images={lightbox.images}
          startIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </main>
  )
}
